import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Zap, X, Volume2 } from 'lucide-react';
import { Button } from './ui/button';

const CompetitionLiveModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [liveCompetition, setLiveCompetition] = useState(null);
  const [prizeName, setPrizeName] = useState('Amazing Prizes');
  const [canPlaySound, setCanPlaySound] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = window.location.origin;

  // Create audio context after user interaction (mobile requirement)
  const initAudioContext = useCallback(() => {
    if (!canPlaySound) {
      setCanPlaySound(true);
    }
  }, [canPlaySound]);

  // Add touch/click listener for audio permission on mobile
  useEffect(() => {
    const handleInteraction = () => {
      initAudioContext();
      // Remove listener after first interaction
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };

    document.addEventListener('touchstart', handleInteraction, { passive: true });
    document.addEventListener('click', handleInteraction, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };
  }, [initAudioContext]);

  const playNotificationSound = useCallback(() => {
    // Create a distinctive bugle/fanfare sound for competition alerts
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume audio context if suspended (mobile requirement)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const masterGain = audioContext.createGain();
      masterGain.connect(audioContext.destination);
      masterGain.gain.setValueAtTime(0.4, audioContext.currentTime);
      
      // Create a celebratory fanfare with multiple notes
      const playNote = (frequency, startTime, duration, volume = 1) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(masterGain);
        
        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = 'triangle'; // Warmer sound than sine
        
        // Envelope for natural sound
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume * 0.5, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
        
        return oscillator;
      };
      
      const now = audioContext.currentTime;
      
      // Victory fanfare melody - 5 note sequence
      playNote(523.25, now, 0.3, 1);        // C5 - Opening
      playNote(659.25, now + 0.15, 0.3, 1); // E5 - Rising
      playNote(783.99, now + 0.30, 0.35, 1.2); // G5 - Peak
      playNote(1046.50, now + 0.50, 0.4, 1.3); // C6 - Climax
      playNote(783.99, now + 0.75, 0.5, 1);  // G5 - Resolution
      
      // Add harmonic richness with octave below (subtle)
      playNote(261.63, now, 0.3, 0.3);        // C4
      playNote(392.00, now + 0.30, 0.35, 0.4); // G4
      playNote(523.25, now + 0.50, 0.4, 0.5); // C5
      
    } catch (error) {
      console.log('Audio notification not available:', error);
    }
  }, []);

  useEffect(() => {
    // Don't show modal on admin pages
    if (location.pathname.startsWith('/admin')) {
      return;
    }

    // Check for live competitions and trigger scheduled ones
    const checkLiveCompetitions = async () => {
      try {
        // First, trigger any scheduled competitions that are ready to start
        try {
          await fetch(`${BACKEND_URL}/api/admin/games/check-scheduled`, {
            method: 'POST'
          });
        } catch (e) {
          // Ignore errors from scheduled check
        }
        
        // Then check for active competitions
        const response = await fetch(`${BACKEND_URL}/api/games/active`);
        if (!response.ok) {
          console.error('Failed to fetch active games');
          return;
        }
        
        const data = await response.json();
        
        if (!data.active_games || !Array.isArray(data.active_games)) {
          return;
        }
        
        // Find games with active competitions
        const liveGames = data.active_games.filter(g => 
          g.competition_mode && 
          g.competition_end && 
          new Date(g.competition_end) > new Date()
        );

        if (liveGames.length > 0) {
          const game = liveGames[0];
          
          // Fetch top prize from admin prizes
          try {
            const prizeResponse = await fetch(`${BACKEND_URL}/api/admin/prizes`);
            if (prizeResponse.ok) {
              const prizeData = await prizeResponse.json();
              const topPrize = prizeData.prizes?.find(p => p.position === 1);
              if (topPrize) {
                setPrizeName(topPrize.prize_name || 'Amazing Prizes');
              }
            }
          } catch (error) {
            console.log('Prize fetch error:', error);
          }
          
          // Check if we've already shown this notification in this session
          // Use sessionStorage for per-session tracking (resets on new tab/window)
          // Use localStorage for persistent tracking
          const currentCompId = `${game.game_id}-${game.competition_start}`;
          const sessionShown = sessionStorage.getItem('competitionModalShown');
          const lastShownId = localStorage.getItem('lastCompetitionNotification');
          
          // Show modal if:
          // 1. Never shown this competition before (localStorage)
          // 2. OR not shown in this session yet (sessionStorage)
          const shouldShow = lastShownId !== currentCompId || sessionShown !== currentCompId;
          
          if (shouldShow) {
            setLiveCompetition(game);
            setIsOpen(true);
            
            // Mark as shown
            localStorage.setItem('lastCompetitionNotification', currentCompId);
            sessionStorage.setItem('competitionModalShown', currentCompId);
            
            // Play notification sound (if user has interacted with page)
            if (canPlaySound) {
              playNotificationSound();
            }
          }
        }
      } catch (error) {
        console.error('Error checking competitions:', error);
      }
    };

    // Check immediately on mount
    checkLiveCompetitions();

    // Then check every 15 seconds (more frequent for better mobile experience)
    const interval = setInterval(checkLiveCompetitions, 15000);

    // Also check when page becomes visible (important for mobile PWA)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkLiveCompetitions();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [BACKEND_URL, canPlaySound, playNotificationSound, location.pathname]);

  const handlePlayNow = () => {
    setIsOpen(false);
    // Play sound on button click (guaranteed user interaction)
    playNotificationSound();
    navigate('/play-games');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen || !liveCompetition) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 p-1 rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-300">
        <div className="bg-gray-900 rounded-2xl p-6 relative overflow-hidden">
          {/* Background Animation */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-yellow-500/10 to-transparent rounded-full animate-pulse"></div>
            <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full animate-pulse delay-75"></div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="relative z-10 text-center">
            {/* Trophy Icon with Animation */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Trophy className="w-16 h-16 sm:w-20 sm:h-20 text-yellow-400 animate-bounce" />
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-300 absolute -top-2 -right-2 animate-ping" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 animate-pulse">
              🎮 GAME IS LIVE! 🎮
            </h2>

            {/* Competition Status */}
            <div className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-4 sm:px-6 py-2 rounded-full mb-4 animate-pulse text-sm sm:text-base">
              COMPETITION MODE ON
            </div>

            {/* Game Details */}
            <div className="bg-black/30 rounded-lg p-4 mb-6 space-y-2">
              <p className="text-xl sm:text-2xl font-bold text-yellow-400">{liveCompetition.game_name}</p>
              <div className="flex items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-300">
                <div>
                  <span className="text-gray-400">Entry Fee:</span>
                  <span className="text-white font-bold ml-1 sm:ml-2">₹{liveCompetition.entry_fee}</span>
                </div>
                <div className="w-px h-4 bg-gray-600"></div>
                <div>
                  <span className="text-gray-400">Win:</span>
                  <span className="text-green-400 font-bold ml-1 sm:ml-2">{prizeName}</span>
                </div>
              </div>
            </div>

            {/* Message */}
            <p className="text-gray-300 mb-6 text-base sm:text-lg">
              Join now and win real gadgets! 🏆
            </p>

            {/* Play Now Button */}
            <Button
              onClick={handlePlayNow}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 text-lg sm:text-xl rounded-xl shadow-lg transform transition-all hover:scale-105 active:scale-95"
              data-testid="competition-play-now-btn"
            >
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 animate-pulse" />
              PLAY NOW
            </Button>

            {/* Sound indicator for mobile */}
            {!canPlaySound && (
              <p className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-1">
                <Volume2 className="w-3 h-3" />
                Tap anywhere for sound
              </p>
            )}

            {/* Close Text */}
            <button
              onClick={handleClose}
              className="mt-4 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionLiveModal;
